import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';
import type { CreateRecipeInput, UpdateRecipeInput, AddRecipeMaterialInput, UpdateRecipeMaterialInput } from '@shared/validation/recipe-schemas';

// ── DTOs ──
export interface RecipeListDTO {
  id: string;
  codigo: string;
  nombre: string;
  categoria: string;
  unidad: string;
  precio_mo_base: number;
  precio_materiales_base: number;
  porcentaje_utilidad_base: number;
  is_active: boolean;
  materials_count: number;
  created_at: string;
  updated_at: string;
}

export interface RecipeMaterialDTO {
  id: string;
  material_id: string;
  material_nombre: string;
  material_sku: string | null;
  material_unidad: string;
  material_precio_unitario: number;
  cantidad_por_unidad: number;
}

export interface RecipeDetailDTO {
  id: string;
  codigo: string;
  nombre: string;
  categoria: string;
  unidad: string;
  precio_mo_base: number;
  precio_materiales_base: number;
  porcentaje_utilidad_base: number;
  is_active: boolean;
  materials: RecipeMaterialDTO[];
  created_at: string;
  updated_at: string;
}

interface RecipesResponse {
  data: RecipeListDTO[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface RecipeFilters {
  search?: string;
  categoria?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

// ── List ──
export function useRecipes(filters?: RecipeFilters) {
  return useQuery({
    queryKey: queryKeys.recipes.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const response = await api.get<RecipesResponse>('/recipes', {
        params: filters,
      });
      return response.data;
    },
  });
}

// ── Detail ──
export function useRecipe(id: string) {
  return useQuery({
    queryKey: queryKeys.recipes.detail(id),
    queryFn: async () => {
      const response = await api.get<{ data: RecipeDetailDTO }>(`/recipes/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

// ── Create ──
export function useCreateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateRecipeInput) => {
      const response = await api.post<{ data: RecipeDetailDTO }>('/recipes', data);
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all });
      toast('success', `Receta "${data.codigo}" creada exitosamente`);
    },
  });
}

// ── Update ──
export function useUpdateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateRecipeInput }) => {
      const response = await api.put<{ data: RecipeDetailDTO }>(`/recipes/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.recipes.detail(variables.id) });
      toast('success', 'Receta actualizada');
    },
  });
}

// ── Add material to recipe ──
export function useAddRecipeMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ recipeId, data }: { recipeId: string; data: AddRecipeMaterialInput }) => {
      const response = await api.post(`/recipes/${recipeId}/materials`, data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.recipes.detail(variables.recipeId) });
      toast('success', 'Material agregado a la receta');
    },
  });
}

// ── Update material quantity in recipe ──
export function useUpdateRecipeMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ recipeId, materialId, data }: { recipeId: string; materialId: string; data: UpdateRecipeMaterialInput }) => {
      const response = await api.put(`/recipes/${recipeId}/materials/${materialId}`, data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.recipes.detail(variables.recipeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all });
      toast('success', 'Cantidad actualizada');
    },
  });
}

// ── Remove material from recipe ──
export function useRemoveRecipeMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ recipeId, materialId }: { recipeId: string; materialId: string }) => {
      const response = await api.delete(`/recipes/${recipeId}/materials/${materialId}`);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.recipes.detail(variables.recipeId) });
      toast('success', 'Material removido de la receta');
    },
  });
}

// ── Delete recipe (soft) ──
export function useDeleteRecipe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/recipes/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all });
      toast('success', 'Receta desactivada');
    },
  });
}
